import { ActionType, createAction, createReducer } from 'typesafe-actions';
import moment from 'moment/moment';
import { tableMock } from 'mocks/tableMock';
import { GameType, ITable, TableStates, TimerTypes } from 'types/tables';
import { IChatMessage } from 'types/chat';
import {
  DealerType,
  ICard,
  IDataResponse,
  TableCommonType,
  ToastType,
  ICardsMap,
  CardPosition,
} from 'types';
import { TablesClosingStatus } from 'constants/tables';
import { messagesQueue, queue } from 'helpers';
import { ACTIONS } from '../constants';

export interface IGameState {
  gameData: ITable;
}

export const gameState: IGameState = {
  gameData: { ...tableMock },
};

export const saveGameDataToStore = createAction(ACTIONS.SAVE_GAME_TO_STORE)<{
  selectedTableData: TableCommonType;
}>();

export const changeDealerAction = createAction(ACTIONS.CHANGE_DEALER)<{ dealerId: string }>();
export const changeDealerFailureAction = createAction(
  ACTIONS.CHANGE_DEALER_FAILURE,
)<IDataResponse>();
export const changeDealerSuccessAction = createAction(ACTIONS.CHANGE_DEALER_SUCCESS)<{
  dealer: DealerType;
}>();

export const changeTimeLeftAction = createAction(ACTIONS.CHANGE_TIME_LEFT)<{
  timerLeft?: number;
}>();
export const decrementTimeAction = createAction(ACTIONS.DECREMENT_TIME_LEFT)<{
  type: TimerTypes;
}>();
export const incrementTimeAction = createAction(ACTIONS.INCREMENT_TIME_LEFT)();

export const startDecrementTimeLeftAction = createAction(ACTIONS.START_DECREMENT_TIME_LEFT)<{
  type: TimerTypes;
}>();
export const stopDecrementTimeLeftAction = createAction(ACTIONS.STOP_DECREMENT_TIME_LEFT)();

export const startIncrementTimeLeftAction = createAction(ACTIONS.START_INCREMENT_TIME_LEFT)();
export const stopIncrementTimeLeftAction = createAction(ACTIONS.STOP_INCREMENT_TIME_LEFT)();

export const startRoundAction = createAction(ACTIONS.START_ROUND)();
export const startRoundSuccessAction = createAction(ACTIONS.START_ROUND_SUCCESS)();
export const cancelRoundAction = createAction(ACTIONS.CANCEL_ROUND)<{ shiftManagerId: string }>();
export const cancelRoundFailureAction = createAction(ACTIONS.CANCEL_ROUND_FAILURE)<IDataResponse>();
export const roundCanceledAction = createAction(ACTIONS.CANCEL_ROUND_SUCCESS)();

export const saveRoundIdToStore = createAction(ACTIONS.SAVE_GAME_ID_TO_STORE)<{
  roundId: string;
}>();
export const saveRoundsResultsToStore = createAction(ACTIONS.SAVE_ROUNDS_RESULTS_TO_STORE)<{
  drawResults: string[] | number[];
}>();
export const changeGameStatusAction = createAction(ACTIONS.CHANGE_GAME_STATUS)<{
  status: string;
}>();
export const changeGameStateAction = createAction(ACTIONS.CHANGE_GAME_STATE)<{
  tableState: TableStates;
}>();

export const bettingTimeStartedAction = createAction(ACTIONS.BETTING_TIME_STARTED)<{
  bettingTime: number;
  finishBettingTime: string;
}>();
export const bettingTimeFinishedAction = createAction(ACTIONS.BETTING_TIME_FINISHED)();

export const startDrawAction = createAction(ACTIONS.START_DRAW)();
export const startDrawSuccessAction = createAction(ACTIONS.START_DRAW_SUCCESS)();
export const startDrawFailureAction = createAction(ACTIONS.START_DRAW_FAILURE)();

export const saveRoundDataToStore = createAction(ACTIONS.SAVE_ROUND_DATA_TO_STORE)<{
  winner: string;
  drawTime: number;
}>();

export const drawTimeStartedAction = createAction(ACTIONS.DRAW_TIME_STARTED)<{
  drawStartedAt: string;
}>();

export const drawFinishedSuccessAction = createAction(ACTIONS.DRAW_FINISHED_SUCCESS)();

export const drawFinishedAction = createAction(ACTIONS.DRAW_FINISHED)<{
  displayingDrawTime: number;
  finishDrawnDisplay: string;
}>();

export const confirmResultAction = createAction(ACTIONS.CONFIRM_RESULT)();
export const confirmResultSuccessAction = createAction(ACTIONS.CONFIRM_RESULT_SUCCESS)<{
  winner: string;
}>();

export const resultConfirmedAction = createAction(ACTIONS.RESULT_CONFIRMED)<{
  displayingWinTime: number;
  finishWinDisplay: string;
}>();

export const roundFinishedSuccessAction = createAction(ACTIONS.ROUND_FINISHED_SUCCESS)();

export const tableCloseVerifyAction = createAction(ACTIONS.CLOSE_TABLE_VERIFY)<{
  shiftManagerId: string;
}>();
export const tableCloseVerifyFailureAction = createAction(
  ACTIONS.CLOSE_TABLE_VERIFY_FAILURE,
)<IDataResponse>();
export const tableCloseVerifySuccessAction = createAction(ACTIONS.CLOSE_TABLE_VERIFY_SUCCESS)();

export const tableCloseAction = createAction(ACTIONS.CLOSE_TABLE)<{
  shiftManagerId: string;
  closingStatus: TablesClosingStatus;
}>();

export const tableCloseSuccessAction = createAction(ACTIONS.CLOSE_TABLE_SUCCESS)();

export const tableCloseNotificationAction = createAction(ACTIONS.CLOSE_TABLE_NOTIFICATION)();

export const changeResultVerifyAction = createAction(ACTIONS.CHANGE_RESULT_VERIFY)<{
  shiftManagerId: string;
}>();
export const changeResultVerifyFailureAction = createAction(
  ACTIONS.CHANGE_RESULT_VERIFY_FAILURE,
)<IDataResponse>();
export const changeResultVerifySuccessAction = createAction(ACTIONS.CHANGE_RESULT_VERIFY_SUCCESS)();
export const changeResultAction = createAction(ACTIONS.CHANGE_RESULT)<{
  shiftManagerId: string;
  winner: string;
}>();
export const changeResultFailureAction = createAction(
  ACTIONS.CHANGE_RESULT_FAILURE,
)<IDataResponse>();
export const changeResultSuccessAction = createAction(ACTIONS.CHANGE_RESULT_SUCCESS)();

export const clearGameDataAction = createAction(ACTIONS.CLEAR_GAME_DATA)();

export const addToastAction = createAction(ACTIONS.ADD_TOAST)<ToastType>();
export const removeToastAction = createAction(ACTIONS.REMOVE_TOAST)<ToastType>();
export const removeAllToastsAction = createAction(ACTIONS.REMOVE_ALL_TOASTS)();

export const saveChatMessagesAction = createAction(ACTIONS.SAVE_CHAT_MESSAGES)<IChatMessage[]>();

export const saveNewChatMessageAction = createAction(ACTIONS.SAVE_NEW_CHAT_MESSAGE)<IChatMessage>();

export const addNewCardAction = createAction(ACTIONS.ADD_NEW_CARD)<{ barcode: string }>();
export const addCardToStoreAction = createAction(ACTIONS.ADD_CARD_TO_STORE)<{
  card: ICard;
  position: CardPosition;
}>();
export const setHasUndefinedCardToStore = createAction(ACTIONS.SET_HAS_UNDEFINED_CARD)<boolean>();
export const setNextCardPositionToStore = createAction(
  ACTIONS.SET_NEXT_CARD_POSITION,
)<CardPosition | null>();
export const clearCardsToStoreAction = createAction(ACTIONS.CLEAR_CARDS_TO_STORE)();
export const setCardsToStoreAction = createAction(ACTIONS.SET_CARDS_TO_STORE)<ICardsMap>();

export const requestShuffleAction = createAction(ACTIONS.SHUFFLE_REQUIRED)();

export const setIsShuffleRequiredAction = createAction(ACTIONS.SET_IS_SHUFFLE_REQUIRED)<boolean>();
export const setIsBurnCardWindowAction = createAction(ACTIONS.SET_IS_BURN_CARD_WINDOW)<boolean>();
export const cancelLastCardUsedAction = createAction(ACTIONS.CANCEL_LAST_CARD_USED)<boolean>();

export const cancelLastCardAction = createAction(ACTIONS.CANCEL_LAST_CARD)<{
  shiftManagerId: string;
}>();
export const cancelLastCardFailureAction = createAction(
  ACTIONS.CANCEL_LAST_CARD_FAILURE,
)<IDataResponse>();
export const cancelLastCardSuccessAction = createAction(ACTIONS.CANCEL_LAST_CARD_SUCCESS)();
export const cancelAllCardsAction = createAction(ACTIONS.CANCEL_ALL_CARDS)<{
  shiftManagerId: string;
}>();
export const cancelAllCardsFailureAction = createAction(
  ACTIONS.CANCEL_ALL_CARDS_FAILURE,
)<IDataResponse>();
export const cancelAllCardsSuccessAction = createAction(ACTIONS.CANCEL_ALL_CARDS_SUCCESS)();
export const changeCardsAction = createAction(ACTIONS.CHANGE_CARDS)<{
  shiftManagerId: string;
  cards: ICardsMap;
}>();
export const changeCardsFailureAction = createAction(ACTIONS.CHANGE_CARDS_FAILURE)<IDataResponse>();
export const changeCardsSuccessAction = createAction(ACTIONS.CHANGE_CARDS_SUCCESS)();

const actions = {
  saveGameDataToStore,
  saveRoundIdToStore,
  changeDealerSuccessAction,
  startRoundSuccessAction,
  roundCanceledAction,
  changeGameStatusAction,
  startDrawSuccessAction,
  drawFinishedSuccessAction,
  saveRoundDataToStore,
  confirmResultSuccessAction,
  roundFinishedSuccessAction,
  bettingTimeStartedAction,
  drawTimeStartedAction,
  changeGameStateAction,
  tableCloseVerifyAction,
  tableCloseVerifySuccessAction,
  tableCloseAction,
  changeResultVerifyAction,
  changeResultVerifySuccessAction,
  changeResultAction,
  changeResultSuccessAction,
  clearGameDataAction,
  saveRoundsResultsToStore,
  tableCloseNotificationAction,
  changeTimeLeftAction,
  decrementTimeAction,
  incrementTimeAction,
  addToastAction,
  removeToastAction,
  removeAllToastsAction,
  saveChatMessagesAction,
  saveNewChatMessageAction,
  drawFinishedAction,
  resultConfirmedAction,
  setHasUndefinedCardToStore,
  setNextCardPositionToStore,
  addCardToStoreAction,
  clearCardsToStoreAction,
  setCardsToStoreAction,
  setIsBurnCardWindowAction,
  setIsShuffleRequiredAction,
  cancelLastCardUsedAction,
};

export const gameReducer = createReducer<IGameState, ActionType<typeof actions>>(gameState)
  .handleAction(saveGameDataToStore, (state, { payload: { selectedTableData } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      game_id: selectedTableData.game_id,
      auto_confirm_result: selectedTableData.auto_confirm_result,
      auto_draw: selectedTableData.auto_draw,
      auto_round: selectedTableData.auto_round,
      betting_time: selectedTableData.betting_time,
      displaying_drawn_result: selectedTableData.displaying_drawn_result,
      displaying_win_result: selectedTableData.displaying_win_result,
      game_type: selectedTableData.game_type,
      rng: selectedTableData.rng,
    },
  }))
  .handleAction(saveRoundsResultsToStore, (state, { payload: { drawResults } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        drawResults,
      },
    },
  }))
  .handleAction(saveRoundIdToStore, (state, { payload: { roundId } }) => ({
    ...state,
    gameData: { ...state.gameData, roundId },
  }))
  .handleAction(changeDealerSuccessAction, (state, { payload: { dealer } }) => ({
    ...state,
    gameData: { ...state.gameData, dealer },
  }))
  .handleAction(
    bettingTimeStartedAction,
    (state, { payload: { bettingTime, finishBettingTime } }) => ({
      ...state,
      gameData: {
        ...state.gameData,
        betting_time: bettingTime,
        round: { ...state.gameData.round, finishBettingTime },
      },
    }),
  )
  .handleAction(
    drawFinishedAction,
    (state, { payload: { finishDrawnDisplay, displayingDrawTime } }) => ({
      ...state,
      gameData: {
        ...state.gameData,
        displaying_drawn_result: displayingDrawTime,
        round: { ...state.gameData.round, finishDrawnDisplay },
      },
    }),
  )
  .handleAction(
    resultConfirmedAction,
    (state, { payload: { displayingWinTime, finishWinDisplay } }) => ({
      ...state,
      gameData: {
        ...state.gameData,
        displaying_win_result: displayingWinTime,
        round: { ...state.gameData.round, finishWinDisplay },
      },
    }),
  )
  .handleAction(drawTimeStartedAction, (state, { payload: { drawStartedAt } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: { ...state.gameData.round, drawStartedAt },
    },
  }))
  .handleAction(changeGameStateAction, (state, { payload: { tableState } }) => ({
    ...state,
    gameData: { ...state.gameData, tableState },
  }))
  .handleAction(changeTimeLeftAction, (state, { payload: { timerLeft } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        timerLeft,
      },
    },
  }))
  .handleAction(decrementTimeAction, (state, { payload: { type } }) => {
    const { timerLeft, finishBettingTime, finishWinDisplay, finishDrawnDisplay } =
      state.gameData.round;

    const finishMoment =
      type === TimerTypes.bettingTime
        ? finishBettingTime
        : type === TimerTypes.displayingDrawn
        ? finishDrawnDisplay
        : finishWinDisplay;

    const finishTime = moment(finishMoment).utc();
    const currentTime = moment.utc();

    const secondsLeft = Math.ceil(finishTime.diff(currentTime, 'milliseconds') / 1000);

    return {
      ...state,
      gameData: {
        ...state.gameData,
        round: {
          ...state.gameData.round,
          timerLeft: timerLeft !== undefined ? secondsLeft : undefined,
        },
      },
    };
  })
  .handleAction(incrementTimeAction, (state) => {
    const { timerLeft } = state.gameData.round;

    return {
      ...state,
      gameData: {
        ...state.gameData,
        round: {
          ...state.gameData.round,
          timerLeft: timerLeft !== undefined ? timerLeft + 1 : undefined,
        },
      },
    };
  })
  .handleAction(roundCanceledAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        drawResults: state.gameData.round.drawResults,
        winner: undefined,
        isFinished: false,
      },
    },
  }))
  .handleAction(startRoundSuccessAction, (state) => ({
    ...state,
    gameData: { ...state.gameData, tableState: TableStates.RoundStarted },
  }))
  .handleAction(startDrawSuccessAction, (state) => ({
    ...state,
    gameData: { ...state.gameData, tableState: TableStates.DrawStarted },
  }))

  .handleAction(drawFinishedSuccessAction, (state) => ({
    ...state,
    gameData: { ...state.gameData, tableState: TableStates.DrawFinished },
  }))
  .handleAction(changeGameStatusAction, (state, { payload: { status } }) => ({
    ...state,
    gameData: { ...state.gameData, statusText: status },
  }))
  .handleAction(saveRoundDataToStore, (state, { payload: { winner, drawTime } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: { ...state.gameData.round, isFinished: true, winner, timeValue: drawTime },
    },
  }))
  .handleAction(confirmResultSuccessAction, (state, { payload: { winner } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        drawResults: queue(
          state.gameData.round.drawResults,
          winner,
          state.gameData.game_type === GameType.ROULETTE ? 5 : 10,
        ),
        timeValue: undefined,
        winner,
      },
    },
  }))
  .handleAction(roundFinishedSuccessAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,

      round: {
        drawResults: state.gameData.round.drawResults,
        isFinished: false,
        winner: undefined,
      },
      changeResult: undefined,
    },
  }))
  .handleAction(tableCloseVerifyAction, (state, { payload: { shiftManagerId } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      closed: {
        shiftManagerId,
      },
    },
  }))
  .handleAction(tableCloseVerifySuccessAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      closed: {
        ...state.gameData.closed,
        verified: true,
      },
    },
  }))
  .handleAction(tableCloseAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      closed: {
        ...state.gameData.closed,
        isInProgress: true,
      },
    },
  }))
  .handleAction(tableCloseNotificationAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      closed: {
        ...state.gameData.closed,
        notification: true,
      },
    },
  }))
  .handleAction(changeResultVerifyAction, (state, { payload: { shiftManagerId } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      changeResult: {
        shiftManagerId,
      },
    },
  }))
  .handleAction(changeResultVerifySuccessAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      changeResult: {
        ...state.gameData.changeResult,
        verified: true,
      },
    },
  }))
  .handleAction(changeResultAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      changeResult: {
        ...state.gameData.changeResult,
        isInProgress: true,
      },
    },
  }))
  .handleAction(changeResultSuccessAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      changeResult: {
        ...state.gameData.changeResult,
        isChanged: true,
      },
    },
  }))
  .handleAction(setHasUndefinedCardToStore, (state, { payload: hasUndefinedCard }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: { ...state.gameData.round, hasUndefinedCard },
    },
  }))
  .handleAction(setNextCardPositionToStore, (state, { payload: position }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: { ...state.gameData.round, activeCardSlot: position },
    },
  }))
  .handleAction(addCardToStoreAction, (state, { payload: { card, position } }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        cards: {
          ...state.gameData.round.cards,
          [position]: card,
        },
      },
    },
  }))
  .handleAction(clearCardsToStoreAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        cards: null,
      },
    },
  }))
  .handleAction(setCardsToStoreAction, (state, { payload: cards }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        cards,
      },
    },
  }))
  .handleAction(setIsBurnCardWindowAction, (state, { payload: isBurnCardWindow }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        isBurnCardWindow,
      },
    },
  }))
  .handleAction(setIsShuffleRequiredAction, (state, { payload: isShuffleRequired }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      isShuffleRequired,
    },
  }))
  .handleAction(cancelLastCardUsedAction, (state, { payload: isUsed }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      round: {
        ...state.gameData.round,
        cancelLastCardUsed: isUsed,
      },
    },
  }))
  .handleAction(addToastAction, (state, { payload }) => {
    const hasSameToast = state.gameData.toastItems.find(
      ({ value }) => value.text === payload.value.text,
    );
    return {
      ...state,
      gameData: {
        ...state.gameData,
        toastItems: hasSameToast
          ? state.gameData.toastItems
          : [...state.gameData.toastItems, payload],
      },
    };
  })
  .handleAction(removeToastAction, (state, { payload }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      toastItems: state.gameData.toastItems.filter(
        ({ value }) => value.text !== payload.value.text,
      ),
    },
  }))
  .handleAction(removeAllToastsAction, (state) => ({
    ...state,
    gameData: {
      ...state.gameData,
      toastItems: [],
    },
  }))
  .handleAction(saveChatMessagesAction, (state, { payload }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      chatMessages: payload,
    },
  }))
  .handleAction(saveNewChatMessageAction, (state, { payload }) => ({
    ...state,
    gameData: {
      ...state.gameData,
      chatMessages: messagesQueue(state.gameData.chatMessages, payload),
    },
  }))
  .handleAction(clearGameDataAction, (state) => ({
    ...state,
    gameData: { ...tableMock },
  }));
